import React, { useMemo, useState } from "react";
import { Box, Button, Tooltip, Typography, styled } from "@mui/material";
import { ContentCopy as CopyIcon, RestartAlt as ResetIcon } from "@mui/icons-material";
import { useIntl } from "react-intl";

import { AiUsageRecordRow } from "../types/MeGenTypes";

import { MissingRateBadge, PriceInput } from "./AiRatePriceCell";
import { PRICE_BOOK, PRICE_BOOK_ISSUES, RAW_PRICE_BOOK, unitSuffixOf } from "./aiModelRates";
import { PRICE_BOOK_PATH, PriceBookExtraModel, exportPriceBookJson } from "./aiPriceBook";
import {
  DEFAULTS_GROUP,
  RateCardRow,
  buildRateCardRows,
  dateRangeLabelOf,
  isBookNewerThanEdits,
  knownKeysOf,
  orphanOverrideKeys,
  pickExportRows,
  unknownModelsFromRecords,
} from "./aiRateCard";
import { formatMetric, formatMoney, serviceLabelOf, toLocalDateTime, toLocalDay } from "./aiUsagePivot";
import { AiModelRatesApi, useAiModelRates } from "./useAiModelRates";
import AiUsageModelLabel from "./AiUsageModelLabel";
import { isAmbiguousWanUsageModel, usageOverrideTargetOf } from "./aiUsageModels";

/**
 * แท็บ "ราคา Model" ของ MeGen Cost — ที่เดียวที่แก้ราคาได้ (บันทึกใน localStorage ของ browser)
 * แสดง price book ทั้งหมด (ค่าเริ่มต้น + ค่าที่แก้), ค่าเริ่มต้นราย provider, model ที่พบใน log แต่ไม่มีในไฟล์
 * และปุ่มคัดลอก JSON เพื่อเอาราคาที่แก้ไปวางทับ aiPriceBook.json ให้เป็นค่าถาวร
 */

// ─── Styled ──────────────────────────────────────────────────────────────────

const Card = styled(Box)({
  backgroundColor: "white",
  borderRadius: "0.75rem",
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  padding: "1.25rem 1.5rem",
  marginBottom: "1rem",
});

const Title = styled(Typography)({
  fontSize: "0.9375rem",
  fontWeight: 700,
  color: "#1e293b",
});

const thTd: React.CSSProperties = {
  padding: "7px 12px",
  fontSize: "0.78rem",
  borderBottom: "1px solid #f1f5f9",
  textAlign: "left",
  verticalAlign: "top",
};
const thStyle: React.CSSProperties = { ...thTd, fontSize: "0.7rem", color: "#64748b", backgroundColor: "#f8fafc", textTransform: "uppercase", letterSpacing: "0.03em", whiteSpace: "nowrap" };
const groupTd: React.CSSProperties = { ...thTd, fontSize: "0.72rem", fontWeight: 700, color: "#475569", backgroundColor: "#f8fafc", letterSpacing: "0.04em", textTransform: "uppercase" };

const Notice: React.FC<{ tone: "warn" | "info"; children: React.ReactNode; id?: string }> = ({ tone, children, id }) => (
  <Box
    id={id}
    sx={{
      mt: 1, px: 1.5, py: 0.75, borderRadius: "0.5rem", fontSize: "0.75rem",
      backgroundColor: tone === "warn" ? "#fef3c7" : "#f1f5f9",
      border: `1px solid ${tone === "warn" ? "#fcd34d" : "#e2e8f0"}`,
      color: tone === "warn" ? "#b45309" : "#475569",
    }}
  >
    {children}
  </Box>
);

const sanitizeId = (s: string) => s.replace(/[^a-zA-Z0-9_-]+/g, "-");

// ─── Row ─────────────────────────────────────────────────────────────────────

const RateCardRowView: React.FC<{ row: RateCardRow; rates: AiModelRatesApi; showUsage?: boolean }> = ({ row, rates, showUsage }) => {
  const intl = useIntl();
  // model นอก price book อาจมีได้หลายแถว (หลาย unit) และ model ใน book อาจมีหลายช่วงวันที่ → id ต้องไม่ซ้ำ
  const idBase = sanitizeId(
    `megen-rates-${row.entryKey}${row.kind === "unknown" && row.unit ? `-${row.unit}` : ""}`
  );
  const hasMissing = row.fields.some((f) => f.price === undefined && !f.stale);
  const unitText = row.unit ? unitSuffixOf(row.unit) : row.free ? "ฟรี" : "-";
  const hasDateWindow = row.startDate !== undefined || row.endDate !== undefined;

  return (
    <tr id={idBase} style={{ backgroundColor: hasMissing ? "#fffbeb" : undefined }}>
      <td style={{ ...thTd, fontFamily: "monospace", minWidth: 200 }}>
        {row.kind === "provider_default" ? (
          <span style={{ fontFamily: "inherit" }}>{row.provider} · model อื่น ๆ</span>
        ) : (
          <>
            <AiUsageModelLabel provider={row.provider} model={row.model} />
            {row.kind === "unknown" && (
              <span style={{ marginLeft: 6, fontSize: "0.68rem", color: "#94a3b8" }}>{row.provider}</span>
            )}
          </>
        )}
        {row.kind === "unknown" && row.fromProviderDefault && (
          <div style={{ fontSize: "0.66rem", color: "#64748b" }}>ใช้ค่าเริ่มต้นของ {row.provider}</div>
        )}
        {row.kind === "unknown" && isAmbiguousWanUsageModel(row.provider, row.model) && (
          <div style={{ fontFamily: "sans-serif", fontSize: "0.7rem", color: "#b45309", marginTop: 4 }}>
            {intl.formatMessage({ id: "megen.cost.ambiguousModel" }, { model: row.model })}
          </div>
        )}
        {hasDateWindow && (
          <div style={{ fontSize: "0.66rem", color: "#64748b", whiteSpace: "nowrap" }}>
            🗓 {dateRangeLabelOf(row.startDate, row.endDate)}
            {row.isCurrent && (
              <span style={{ marginLeft: 6, color: "#16a34a", fontWeight: 600 }}>ช่วงที่ใช้อยู่</span>
            )}
          </div>
        )}
        {row.note && (
          <Tooltip title={row.note}>
            <span style={{ marginLeft: 6, fontSize: "0.7rem", color: "#94a3b8", cursor: "help" }}>ⓘ</span>
          </Tooltip>
        )}
        {row.source && (
          <a href={row.source} target="_blank" rel="noreferrer" style={{ marginLeft: 6, fontSize: "0.66rem", color: "#64748b", textDecoration: "underline dotted" }}>
            ที่มา
          </a>
        )}
      </td>
      <td style={{ ...thTd, whiteSpace: "nowrap" }}>{row.service ? serviceLabelOf(row.service) : "-"}</td>
      <td style={{ ...thTd, whiteSpace: "nowrap", color: "#64748b" }}>
        {unitText}
        {row.free && row.unit && <span style={{ marginLeft: 6, fontSize: "0.68rem", color: "#16a34a" }}>ฟรี</span>}
      </td>
      <td style={{ ...thTd, minWidth: 320 }}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
          {row.fields.map((f) => (
            <Box key={f.variant} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {f.label && <span style={{ fontSize: "0.7rem", color: "#64748b", whiteSpace: "nowrap" }}>{f.label}</span>}
              {f.stale ? (
                <Tooltip title="variant นี้ไม่ตรงกับ unit ปัจจุบันของ model (price book เปลี่ยน) — ไม่ถูกใช้ กด คืนค่า เพื่อลบ">
                  <span style={{ fontSize: "0.72rem", color: "#94a3b8", textDecoration: "line-through", cursor: "help" }}>
                    {f.variant} {formatMoney(f.price || 0)}
                  </span>
                </Tooltip>
              ) : (
                <>
                  <span style={{ fontSize: "0.75rem", color: "#64748b" }}>$</span>
                  <PriceInput
                    id={sanitizeId(`${idBase}-${f.variant}`)}
                    value={f.price}
                    overridden={f.overridden}
                    missing={f.price === undefined}
                    placeholder={row.unit === "gpu_hour" ? "USD/ชม." : undefined}
                    onCommit={(usd) => rates.setPrice(row.entryKey, f.variant, usd)}
                  />
                  {f.overridden && (
                    <span style={{ fontSize: "0.65rem", color: "#94a3b8", whiteSpace: "nowrap" }}>
                      ค่าเริ่มต้น {f.defaultPrice === undefined ? "ยังไม่ตั้ง" : formatMoney(f.defaultPrice)}
                    </span>
                  )}
                  {f.price === undefined && <MissingRateBadge />}
                </>
              )}
            </Box>
          ))}
          {!row.fields.length && row.free && <span style={{ fontSize: "0.72rem", color: "#16a34a" }}>ฟรี — ไม่มีช่องกรอก</span>}
        </Box>
      </td>
      {showUsage && (
        <td style={{ ...thTd, whiteSpace: "nowrap", color: "#64748b" }}>
          {formatMetric(row.calls || 0)} call
          {row.lastUsed ? ` · ${toLocalDateTime(row.lastUsed)}` : ""}
        </td>
      )}
      <td style={{ ...thTd, whiteSpace: "nowrap" }}>
        {row.overriddenCount > 0 && (
          <Typography
            id={`${idBase}-reset`}
            onClick={() =>
              // model นอก price book: 1 key อาจมีหลายแถว (แถวละ unit) → คืนค่าเฉพาะช่องของแถวนี้
              row.kind === "unknown"
                ? rates.resetVariants(
                    row.entryKey,
                    row.fields.filter((f) => f.overridden).map((f) => f.variant)
                  )
                : rates.resetRate(row.entryKey)
            }
            sx={{ fontSize: "0.7rem", color: "#db2777", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
          >
            ↺ คืนค่า
          </Typography>
        )}
      </td>
    </tr>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────

type CardAiRateCardProps = {
  records: AiUsageRecordRow[]; // Filtered display records for unknown models and their usage.
  allRecords?: AiUsageRecordRow[]; // Full inventory for export and orphan checks, independent of display filters.
  rangeLabel?: string; // เช่น "2026-08-04 ถึง 2026-09-02"
};

const CardAiRateCard: React.FC<CardAiRateCardProps> = ({ records, allRecords = records, rangeLabel }) => {
  const rates = useAiModelRates();
  // snapshot ของการคัดลอกครั้งล่าสุด — ผูกกับ rates.version เพื่อรู้ว่าราคาเปลี่ยนหลังคัดลอกไปแล้วหรือยัง
  const [copySnapshot, setCopySnapshot] = useState<{ version: number; message: string; text: string } | null>(null);
  const [showJson, setShowJson] = useState(false);
  const copyStale = !!copySnapshot && copySnapshot.version !== rates.version;

  const groups = useMemo(() => buildRateCardRows(PRICE_BOOK, rates.overrides), [rates.overrides]);
  const unknown = useMemo(() => unknownModelsFromRecords(records, rates.overrides), [records, rates.overrides]);
  const inventory = useMemo(() => unknownModelsFromRecords(allRecords, rates.overrides), [allRecords, rates.overrides]);
  const orphans = useMemo(
    () => orphanOverrideKeys(rates.overrides, knownKeysOf(groups, inventory)),
    [rates.overrides, groups, inventory]
  );

  const bookNewerThanEdits = isBookNewerThanEdits(rates.savedAt, PRICE_BOOK.updated);
  const canExport = rates.overrideCount > 0;

  const buildExport = () => {
    // 1 model = 1 entry ใน price book แม้ log จะมีหลาย unit (ดู pickExportRows)
    const extraModels: PriceBookExtraModel[] = pickExportRows(inventory)
      .filter((u): u is RateCardRow & { unit: NonNullable<RateCardRow["unit"]> } => !!u.unit)
      .map((u) => ({ provider: u.provider, model: u.model, service: u.service || undefined, unit: u.unit }));
    return exportPriceBookJson({
      raw: RAW_PRICE_BOOK,
      book: PRICE_BOOK,
      overrides: rates.overrides,
      extraModels,
      today: toLocalDay(new Date().toISOString()), // วันท้องถิ่น — ไม่ใช่วัน UTC (บั๊กชนิดเดียวกับแบนเนอร์ book newer)
    });
  };

  const handleCopy = async () => {
    const ex = buildExport();
    const version = rates.version;
    const summary = `${ex.changedModels} model เปลี่ยน · ${ex.addedModels} เพิ่มใหม่ · ${ex.changedDefaults} ค่าเริ่มต้น provider${
      ex.skipped.length ? ` · ข้าม ${ex.skipped.length} รายการ (${ex.skipped[0]})` : ""
    }`;
    const manual = `คัดลอกอัตโนมัติไม่ได้ — เลือกข้อความด้านล่างแล้วคัดลอกเอง · ${summary}`;
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(ex.text);
        setCopySnapshot({ version, message: `คัดลอกแล้ว · ${summary}`, text: ex.text });
        setShowJson(true);
      } else {
        setShowJson(true);
        setCopySnapshot({ version, message: manual, text: ex.text });
      }
    } catch {
      setShowJson(true);
      setCopySnapshot({ version, message: manual, text: ex.text });
    }
  };

  const linkSx = { fontSize: "0.75rem", color: "#db2777", cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap", "&:hover": { textDecoration: "underline" } };

  return (
    <Box id="megen-rate-card">
      {/* Header */}
      <Card>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
          <Box>
            <Title>ราคา Model (price book) — แก้ราคาที่นี่ที่เดียว</Title>
            <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8", mt: 0.25 }}>
              ค่าเริ่มต้นจาก {PRICE_BOOK_PATH}{PRICE_BOOK.updated ? ` (อัปเดต ${PRICE_BOOK.updated})` : ""} · USD · {PRICE_BOOK.models.length} model ·{" "}
              {Object.keys(PRICE_BOOK.providerDefaults).length} ค่าเริ่มต้นราย provider
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: rates.overrideCount ? "#be185d" : "#64748b", mt: 0.25, fontWeight: rates.overrideCount ? 600 : 400 }}>
              {rates.overrideCount
                ? `● แก้แล้ว ${rates.overrideCount} ค่า · บันทึกใน browser นี้ (localStorage)${rates.savedAt ? ` · ล่าสุด ${toLocalDateTime(rates.savedAt)}` : ""}`
                : "ยังไม่มีค่าที่แก้ — ราคาที่แก้จะบันทึกใน browser นี้ (localStorage) และมีผลทุกหน้าของ MeGen Cost และ modal AI Usage ทันที"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            {rates.hasOverrides && (
              <Typography id="megen-rates-reset-all" onClick={rates.resetAll} sx={linkSx}>
                <ResetIcon sx={{ fontSize: 14, verticalAlign: "text-bottom", mr: 0.25 }} />
                คืนค่าเริ่มต้นทั้งหมด ({rates.overrideCount} ค่า)
              </Typography>
            )}
            <Tooltip title={canExport ? "รวมราคาที่แก้ใน browser เข้ากับ price book แล้วคัดลอกเป็น JSON — วางทับทั้งไฟล์ aiPriceBook.json แล้ว build ใหม่ ราคาจะเป็นค่าเริ่มต้นถาวร" : "ยังไม่มีราคาที่แก้"}>
              <span>
                <Button
                  id="megen-rates-copy-json"
                  size="small"
                  variant="outlined"
                  disabled={!canExport}
                  startIcon={<CopyIcon sx={{ fontSize: 14 }} />}
                  onClick={handleCopy}
                  sx={{ textTransform: "none", fontSize: "0.75rem", borderRadius: "9999px", py: 0.25, px: 1.5, color: "#db2777", borderColor: "#f9a8d4", "&:hover": { borderColor: "#db2777", backgroundColor: "#fdf2f8" } }}
                >
                  คัดลอก JSON สำหรับ price book
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {rates.storageStatus !== "ok" && (
          <Notice tone="warn" id="megen-rates-storage-warning">
            ⚠ บันทึกลง browser ไม่ได้ ({rates.storageStatus === "unavailable" ? "localStorage ถูกปิด/ใช้ไม่ได้" : "เขียน localStorage ไม่สำเร็จ"}) — ราคาที่แก้จะหายเมื่อ reload
          </Notice>
        )}
        {PRICE_BOOK_ISSUES.length > 0 && (
          <Notice tone="warn" id="megen-rates-book-issues">
            ⚠ price book มี {PRICE_BOOK_ISSUES.length} รายการที่ควรแก้ในไฟล์:
            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
              {PRICE_BOOK_ISSUES.slice(0, 5).map((i) => (
                <li key={i}>{i}</li>
              ))}
              {PRICE_BOOK_ISSUES.length > 5 && <li>… อีก {PRICE_BOOK_ISSUES.length - 5} รายการ (ดู console)</li>}
            </ul>
          </Notice>
        )}
        {bookNewerThanEdits && (
          <Notice tone="warn" id="megen-rates-book-newer">
            ⚠ price book อัปเดต ({PRICE_BOOK.updated}) หลังจากที่คุณแก้ราคาใน browser ({toLocalDateTime(rates.savedAt)}) — ตรวจสอบค่าที่แก้ไว้ว่ายังต้องการหรือไม่
          </Notice>
        )}
        {copySnapshot && !copyStale && (
          <Notice tone="info" id="megen-rates-copy-status">
            {copySnapshot.message} · วางทับทั้งไฟล์ {PRICE_BOOK_PATH} แล้ว build ใหม่ (_readme ถูกคงไว้){" "}
            <span onClick={() => setShowJson((s) => !s)} style={{ color: "#db2777", cursor: "pointer", fontWeight: 600 }}>
              {showJson ? "ซ่อน JSON" : "ดู JSON"}
            </span>
          </Notice>
        )}
        {copyStale && canExport && (
          <Notice tone="warn" id="megen-rates-copy-stale">
            ⚠ ราคาเปลี่ยนหลังจากที่คัดลอก JSON ไป — ของที่คัดลอกไว้เป็นค่าเก่า กด "คัดลอก JSON สำหรับ price book" อีกครั้ง
          </Notice>
        )}
        {showJson && !copyStale && copySnapshot?.text && (
          <textarea
            id="megen-rates-json"
            readOnly
            value={copySnapshot.text}
            onFocus={(e) => e.target.select()}
            style={{ width: "100%", minHeight: 220, marginTop: 8, fontFamily: "monospace", fontSize: "0.7rem", border: "1px solid #e2e8f0", borderRadius: 8, padding: 8, boxSizing: "border-box" }}
          />
        )}
      </Card>

      {/* Price book */}
      <Card>
        <Title sx={{ mb: 1 }}>ราคาต่อหน่วยตาม price book</Title>
        <Box sx={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: "900px" }}>
            <thead>
              <tr>
                <th style={thStyle}>Model</th>
                <th style={thStyle}>Service</th>
                <th style={thStyle}>หน่วย</th>
                <th style={thStyle}>ราคา (USD) — แก้ได้ทันที</th>
                <th style={thStyle} />
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <React.Fragment key={g.provider}>
                  <tr>
                    <td style={groupTd} colSpan={5}>
                      {g.label}
                      {g.provider === DEFAULTS_GROUP && (
                        <span style={{ marginLeft: 8, fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "#94a3b8" }}>
                          ใช้กับ model ของ provider นั้นที่ไม่มีในรายการด้านบน (เช่น model ใหม่)
                        </span>
                      )}
                    </td>
                  </tr>
                  {g.rows.map((row) => (
                    <RateCardRowView key={row.entryKey} row={row} rates={rates} />
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </Box>
      </Card>

      {/* Unknown models from log */}
      <Card>
        <Title sx={{ mb: 0.25 }}>Model ที่พบใน log แต่ไม่มีใน price book</Title>
        <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8", mb: 1 }}>
          จาก {formatMetric(records.length)} records ที่โหลดอยู่{rangeLabel ? ` (${rangeLabel})` : ""} · กรอกราคาที่นี่ได้เลย แล้วกด "คัดลอก JSON" เพื่อเพิ่มลง price book ถาวร
        </Typography>
        {unknown.length === 0 ? (
          <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8", py: 1 }}>— ทุก model ใน log มีราคาแล้ว —</Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: "900px" }}>
              <thead>
                <tr>
                  <th style={thStyle}>Model · Provider</th>
                  <th style={thStyle}>Service</th>
                  <th style={thStyle}>หน่วย (เดาจากการใช้งาน)</th>
                  <th style={thStyle}>ราคา (USD)</th>
                  <th style={thStyle}>การใช้งาน</th>
                  <th style={thStyle} />
                </tr>
              </thead>
              <tbody>
                {unknown.map((row) => (
                  <RateCardRowView key={`${row.key}|${row.unit || ""}`} row={row} rates={rates} showUsage />
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </Card>

      {/* Orphan overrides */}
      {orphans.length > 0 && (
        <Card>
          <Title sx={{ mb: 0.5 }}>ราคาที่แก้ไว้แต่ไม่ตรงกับ price book หรือ log ปัจจุบัน</Title>
          <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8", mb: 1 }}>
            model เหล่านี้อาจถูกลบ/เปลี่ยนชื่อใน price book หรือไม่ได้ใช้ในช่วงเวลาที่โหลด — ลบได้ถ้าไม่ต้องการแล้ว
          </Typography>
          {orphans.map((key) => (
            <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.25 }}>
              <span style={{ fontFamily: "monospace", fontSize: "0.75rem", color: "#334155" }}>{key}</span>
              <span style={{ fontSize: "0.72rem", color: "#64748b" }}>
                {Object.entries(rates.overrides[key] || {})
                  .map(([variant, usd]) => `${variant} ${formatMoney(usd)}`)
                  .join(" · ")}
              </span>
              <Typography id={sanitizeId(`megen-rates-orphan-${key}-delete`)} onClick={() => rates.resetRate(key)} sx={linkSx}>
                ลบ
              </Typography>
              <LegacyOverrideNotice overrideKey={key} />
            </Box>
          ))}
        </Card>
      )}
    </Box>
  );
};

const LegacyOverrideNotice: React.FC<{ overrideKey: string }> = ({ overrideKey }) => {
  const intl = useIntl();
  const target = usageOverrideTargetOf(overrideKey);
  return target ? <Typography variant="caption" sx={{ color: "#b45309" }}>{intl.formatMessage({ id: "megen.cost.legacyOverride" }, { target })}</Typography> : null;
};

export default CardAiRateCard;
